export const PipelineRouteKey = {
  Investment: 'investment',
  Deals: 'deals',
} as const;
export type PipelineRouteKey = (typeof PipelineRouteKey)[keyof typeof PipelineRouteKey];

export type PipelineTone =
  | 'default'
  | 'blue'
  | 'purple'
  | 'amber'
  | 'teal'
  | 'green'
  | 'red'
  | 'neutral';

export type PipelineOption = {
  value: string;
  label: string;
  disabled?: boolean;
  terminal?: boolean;
};

type PipelineFilterKey<TFilters> = Extract<keyof TFilters, string>;

export type PipelineFilterDefinition<TFilters extends object = Record<string, unknown>> = {
  key: PipelineFilterKey<TFilters>;
  urlKey: string;
  label: string;
  type: 'staticMultiSelect' | 'boolean';
  options?: PipelineOption[];
  controlSize?: 'small';
  includeOption?: {
    option: PipelineOption;
    flagKey: PipelineFilterKey<TFilters>;
  };
};

export type PipelineCardProperty =
  | { key: string; kind: 'text'; text: string }
  | {
      key: string;
      kind: 'tags';
      tags: Array<{
        key: string;
        label: string;
        tone?: PipelineTone;
        variant?: 'filled' | 'muted' | 'outline' | 'dashed';
      }>;
    };

export type PipelineCard = {
  id: string;
  // Widened beyond PipelineRouteKey so non-Gamma consumers (e.g. a mock-backed
  // fund-scoped board) can plug in their own pipeline identifier without
  // extending the Gamma admin route enum.
  pipeline: PipelineRouteKey | (string & {});
  stage: string;
  active: boolean;
  variant?: 'default' | 'accent' | 'dashed';
  accentTone?: PipelineTone;
  /** Draws a full-card outline in this tone. Reserved for "needs attention now", not decoration. */
  outlineTone?: PipelineTone;
  mixedTone?: boolean;
  title: string;
  titleTone?: PipelineTone;
  avatar?: {
    url: string | null;
    label: string;
    icon: 'user' | 'briefcase';
  };
  subtitleLines: string[];
  badges: Array<{
    key: string;
    label: string;
    tone: PipelineTone;
    variant?: 'filled' | 'muted' | 'outline' | 'dashed';
  }>;
  properties?: PipelineCardProperty[];
  indicators: Array<{
    key: string;
    label: string;
    icon?: 'eye' | 'message' | 'flag' | 'user';
    active: boolean;
  }>;
  timer: {
    label: string;
    tone: 'green' | 'yellow' | 'amber' | 'red';
    expiresAt?: string | null;
  } | null;
  lastStageChangedAt: string | null;
  entityRefs: Record<string, string | undefined>;
};

export type PipelineColumn = {
  stage: string;
  label: string;
  compactLabel?: string;
  order: number;
  terminal: boolean;
  count: number;
  cards: PipelineCard[];
};

export type PipelineDragMove = {
  cardId: string;
  sourceStage: string;
  sourceIndex: number;
  targetStage: string;
  targetIndex: number;
};

export type PipelineBoardSortDirection = 'asc' | 'desc';

export type PipelineCardComparator = (
  _left: PipelineCard,
  _right: PipelineCard,
  _direction: PipelineBoardSortDirection,
) => number;

export type PipelineBoardSort = {
  active: boolean;
  direction: PipelineBoardSortDirection;
  compareCards: PipelineCardComparator;
};

export type PipelineBaseBoardFilters = {
  search?: string;
  activeOnly?: boolean;
  stages?: string[];
};

export type PipelineUrlSelection = {
  card?: string;
};

export type PipelineBaseUrlFilters = PipelineBaseBoardFilters & PipelineUrlSelection;

export type PipelineBoard = {
  pipeline: PipelineRouteKey | (string & {});
  columns: PipelineColumn[];
  filterDefinitions: PipelineFilterDefinition[];
  totals: {
    cards: number;
    activeCards: number;
    nonActiveCards: number;
  };
};

export type PipelineCardDetail = PipelineCard & {
  headerLinks?: {
    titleHref?: string | null;
    subtitleLineHrefs?: Array<string | null>;
  };
  headerActions: Array<{
    key: string;
    label: string;
    href: string | null;
    disabled?: boolean;
    placement?: 'header' | 'inlineMeta' | 'primary';
  }>;
  sections: Array<{
    key: string;
    title: string;
    rows: Array<{
      key: string;
      label: string;
      value: string | null;
      tone?: PipelineTone;
      href?: string | null;
    }>;
  }>;
  timeline: Array<{
    stage: string;
    label: string;
    reachedAt: string | null;
    highlighted: boolean;
    source: 'Automatic' | 'Manual' | null;
    triggerEventName: string | null;
    note: string | null;
  }>;
};
